/**
 * Shared reference data: categories, doctors, patients, team members, packages.
 *
 * Replaces the legacy `loadData()` plus the sprawling `window.*` globals it
 * populated (`window.doctors`, `window.patients`, `window.categories`,
 * `window.teamMembers`, `window.packages`) and the `smart_refresh.js`
 * re-hydration pass that reloaded all of them.
 *
 * Two behaviours from the legacy system are deliberately kept:
 *   - each collection loads independently, so one failing endpoint does not
 *     blank the whole app;
 *   - a manual "smart refresh" re-fetches everything and keeps the previous
 *     values if a call fails, rather than showing zeros.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { categoryApi, doctorApi, syncApi, teamApi, adminApi } from '../api/endpoints';
import {
  mergeCategories,
  normalizeDoctors,
  normalizeTeamMembers,
  withDoctorCounts,
  DEFAULT_CATEGORIES,
} from '../domain/catalog';
import { ROLES, useAuth } from './AuthContext';

const DataContext = createContext(null);

/** One collection's loading state. */
const initialResource = (fallback) => ({
  data: fallback,
  loading: false,
  error: null,
  loadedAt: null,
});

export function DataProvider({ children }) {
  const { isAuthenticated, role } = useAuth();

  const [categories, setCategories] = useState(() => initialResource(DEFAULT_CATEGORIES));
  const [doctors, setDoctors] = useState(() => initialResource([]));
  const [patients, setPatients] = useState(() => initialResource([]));
  const [teamMembers, setTeamMembers] = useState(() => initialResource([]));
  const [packages, setPackages] = useState(() => initialResource([]));
  const [refreshing, setRefreshing] = useState(false);

  // Guards against a state update landing after the provider unmounts.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Run one fetch into one resource slot.
   * On failure the previous data is retained — a failed refresh must never
   * replace real data with an empty list.
   */
  const loadResource = useCallback(async (setter, fetcher, transform, label) => {
    setter((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const raw = await fetcher();
      const data = transform ? transform(raw) : raw;
      if (mounted.current) {
        setter({ data, loading: false, error: null, loadedAt: Date.now() });
      }
      return data;
    } catch (err) {
      console.error(`[Data] Failed to load ${label}:`, err.message);
      if (mounted.current) {
        setter((prev) => ({ ...prev, loading: false, error: err.message }));
      }
      return null;
    }
  }, []);

  const loadCategories = useCallback(
    () => loadResource(setCategories, categoryApi.list, mergeCategories, 'categories'),
    [loadResource],
  );

  const loadDoctors = useCallback(
    () => loadResource(setDoctors, doctorApi.list, normalizeDoctors, 'doctors'),
    [loadResource],
  );

  const loadPatients = useCallback(
    () => loadResource(setPatients, syncApi.patients, null, 'patients'),
    [loadResource],
  );

  const loadTeamMembers = useCallback(
    () => loadResource(setTeamMembers, teamApi.members, normalizeTeamMembers, 'team members'),
    [loadResource],
  );

  const loadPackages = useCallback(
    () => loadResource(setPackages, doctorApi.packages, null, 'packages'),
    [loadResource],
  );

  /* ---------------------------------------------------------------------- */
  /* Public data loads for everyone; the rest waits for a token.             */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    loadCategories();
    loadDoctors();
    loadPackages();
  }, [loadCategories, loadDoctors, loadPackages]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadPatients();
    // The team roster is only meaningful to staff roles.
    if (role === ROLES.ADMIN || role === ROLES.TEAM_MEMBER) loadTeamMembers();
  }, [isAuthenticated, role, loadPatients, loadTeamMembers]);

  /**
   * Smart refresh — the legacy `triggerSmartRefresh`, minus the cache-purging
   * and particle animation. React state is the cache, so re-fetching is enough.
   */
  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    const tasks = [loadCategories(), loadDoctors(), loadPackages()];
    if (isAuthenticated) {
      tasks.push(loadPatients());
      if (role === ROLES.ADMIN || role === ROLES.TEAM_MEMBER) tasks.push(loadTeamMembers());
    }
    await Promise.allSettled(tasks);
    if (mounted.current) setRefreshing(false);
  }, [
    isAuthenticated,
    role,
    loadCategories,
    loadDoctors,
    loadPackages,
    loadPatients,
    loadTeamMembers,
  ]);

  /** Categories with live doctor counts, so the specialty grid is never stale. */
  const categoriesWithCounts = useMemo(
    () => withDoctorCounts(categories.data, doctors.data),
    [categories.data, doctors.data],
  );

  const findDoctor = useCallback(
    (id) => doctors.data.find((d) => String(d.id) === String(id)) || null,
    [doctors.data],
  );

  const findPatient = useCallback(
    (id) => patients.data.find((p) => String(p.id) === String(id)) || null,
    [patients.data],
  );

  const doctorsByCategory = useCallback(
    (categoryId) => doctors.data.filter((d) => d.category === categoryId),
    [doctors.data],
  );

  const value = useMemo(
    () => ({
      categories: categoriesWithCounts,
      categoriesState: categories,
      doctors: doctors.data,
      doctorsState: doctors,
      patients: patients.data,
      patientsState: patients,
      teamMembers: teamMembers.data,
      teamMembersState: teamMembers,
      packages: packages.data,
      packagesState: packages,

      refreshing,
      refreshAll,
      loadCategories,
      loadDoctors,
      loadPatients,
      loadTeamMembers,
      loadPackages,

      findDoctor,
      findPatient,
      doctorsByCategory,
    }),
    [
      categoriesWithCounts,
      categories,
      doctors,
      patients,
      teamMembers,
      packages,
      refreshing,
      refreshAll,
      loadCategories,
      loadDoctors,
      loadPatients,
      loadTeamMembers,
      loadPackages,
      findDoctor,
      findPatient,
      doctorsByCategory,
    ],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used inside a <DataProvider>');
  return context;
}

/** Admin-only doctor list (every status, not just active). */
export function useAllDoctors() {
  const [state, setState] = useState(() => initialResource([]));

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await adminApi.allDoctors();
      setState({ data: data || [], loading: false, error: null, loadedAt: Date.now() });
      return data;
    } catch (err) {
      console.error('[Data] Failed to load the full doctor list:', err.message);
      setState((prev) => ({ ...prev, loading: false, error: err.message }));
      return null;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}
